import price from "./calculatePrice";

/**
 * Calculates GST charges with discount and other charges.
 * @param {number} amount - The base amount.
 * @param {number} gstRate - GST rate in percent.
 * @param {boolean} isInclusive - If true, GST is inclusive; else exclusive.
 * @param {number} discount - Discount to subtract.
 * @param {number} otherCharges - Charges to add.
 * @returns {object} - { gstAmount, totalAmount }
 */
function calculateGST(amount, gstRate, isExclusive = true, discount = 0, otherCharges = 0) {
  const adjustedAmount = amount;
  let gstAmount, totalAmount;
  if (isExclusive) {
    gstAmount = adjustedAmount * (gstRate / 100);
    totalAmount = adjustedAmount + gstAmount;
  } else {
    gstAmount = adjustedAmount - (adjustedAmount / (1 + gstRate / 100));
    totalAmount = adjustedAmount;
  }
  return {
    gstAmount: parseFloat(gstAmount.toFixed(2)),
    totalAmount: parseFloat(totalAmount.toFixed(2)),
  };
}

/**
 * Calculates order totals including taxes, discounts, and other charges.
 * @param {object} activeOrder - The active order object containing order items.
 * @param {number} cgst - CGST rate in percent.
 * @param {number} sgst - SGST rate in percent.
 * @param {number} otherCharges - Additional charges to add to the total.
 * @param {number} discount - Discount to subtract from the total.
 * @param {string} tableType - Type of table (for price calculation).
 * @returns {object} - Object containing detailed totals.
 */

const calculateOrderTotals = (
  activeOrder,
  cgst,
  sgst,
  otherCharges,
  discount,
  tableType,
  exclusiveGST,
) => {
  let itemsTotal = 0;
  let cgstCharges = 0;

  let sgstCharges = 0;
  let grandTotal = 0;
  let grandTotalWithoutDiscount = 0;

  let tablePrice = 0;
  let taxTotal = 0;
  let subTotal  = 0;
  const chargeDifference = otherCharges - discount;
  let gstItemsTotal = 0;

  if (activeOrder?.orderItems) {
    // Calculate total price for all items (gstItemsTotal)
    activeOrder?.orderItems.forEach((item) => {
      gstItemsTotal += item.quantity * (price(tableType, item) || item.itemPrice);
    });

    // Distribute chargeDifference proportionally to each item
    activeOrder?.orderItems.forEach((item) => {
      const itemBaseTotal = item.quantity * (price(tableType, item) || item.itemPrice);
      // Proportional share of chargeDifference
      const itemChargeShare = gstItemsTotal > 0 ? (chargeDifference * (itemBaseTotal / gstItemsTotal)) : 0;
      const itemTotalWithCharges = itemBaseTotal + itemChargeShare;

      // Calculate GST for this item (exclusive GST)
      const { gstAmount, totalAmount } = calculateGST(
        itemTotalWithCharges,
        (item?.cgst || 0) + (item?.sgst || 0),
        exclusiveGST // false = inclusive / true = exclusive GST
      );

      itemsTotal += exclusiveGST ? totalAmount : itemTotalWithCharges;
      // subTotal += exclusiveGST ? totalAmount : (totalAmount - gstAmount); // or use totalAmount if you want GST included
      subTotal += itemBaseTotal;
      // Split GST equally between CGST and SGST
      cgstCharges += parseFloat((gstAmount / 2).toFixed(2));
      sgstCharges += parseFloat((gstAmount / 2).toFixed(2));
    });

    tablePrice = activeOrder.tablePrice;
  }

  grandTotal = itemsTotal;
  grandTotalWithoutDiscount = gstItemsTotal + parseFloat(otherCharges || 0);

  taxTotal = parseFloat(cgstCharges + sgstCharges);

  return {
    itemsTotal,
    subTotal,
    cgstCharges,
    sgstCharges,
    otherCharges,
    taxTotal,
    gstItemsTotal,
    discount,
    tablePrice,
    refId: activeOrder?.refId,
    grandTotal: grandTotal.toFixed(2),
    grandTotalWithoutDiscount: grandTotalWithoutDiscount.toFixed(2),
    cgst,
    sgst,
  };
};

export default calculateOrderTotals;
